import { types as t } from 'mst-effect';
import { withRunInAction } from 'utils/withRunInAction';
import { Annotation, Input } from 'api/grpc/resources';
import { PER_PAGE } from 'modules/InputViewer/utils/constants';
import { MultiSearchResponse } from 'api/grpc/service';
import { either } from 'fp-ts';
import { pipe } from 'utils/fp';
import isEqual from 'lodash/isEqual';
import { toJS } from 'mobx';
import { getInputPagedIndex, getPageNumberByIndex } from 'modules/InputViewer/utils/helpers';

export type AnnotationWithInput = {
  annotation: Annotation;
  input: Input;
};

const getRespAnnotationsPage = (listE: either.Either<Error, MultiSearchResponse>): AnnotationWithInput[] | null => {
  return pipe(
    listE,
    either.match(
      // onLeft
      () => {
        console.warn('getRespAnnotationsPage: something went wrong');
        return null;
      },
      // onRight
      (resp) => {
        return resp.getHitsList().map((hit) => {
          // non-null assertion is fine because the API always returns an annotation for each hit
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return {
            annotation: hit.getAnnotation(),
            input: hit.getInput(),
          } as AnnotationWithInput;
        });
      },
    ),
  );
};
const CollaboratorModel = t.model('CollaboratorModel', {
  userId: t.string,
  firstName: t.string,
  lastName: t.string,
});

export const CollaboratorsListMapModel = t.map(CollaboratorModel);

export const AnnotationsListStore = t
  .compose(
    withRunInAction(),
    t.model('AnnotationsListStore', {
      annotationsListRecords: t.map(t.frozen()),
      scoresAnnotationsListRecord: t.map(t.frozen()),
      inputsListForAnnotations: t.map(t.frozen()),
      isAnnotationsLoading: t.maybeNull(t.boolean),
      hasAnnotationsListReachedEnd: t.maybeNull(t.boolean),
      lastAnnotationsListPage: t.maybeNull(t.number),
      currentAnnotationsListPage: t.optional(t.number, 1),
      annotationsPagesStartedLoad: t.optional(t.array(t.number), []),
      annotationsListError: t.maybe(t.frozen<Error>()),
      isCollaboratorsListLoading: t.optional(t.boolean, false),
      isCollaboratorsListLoaded: t.optional(t.boolean, false),
      collaboratorsListMap: CollaboratorsListMapModel,
    }),
  )
  .views((self) => ({
    get lastAnnotationsPageNumber() {
      const annotationsList = toJS(self.annotationsListRecords);
      const annotationsListKeys = Array.from(annotationsList.keys());
      const sortedAnnotationsListKeys = annotationsListKeys.sort((a, b) => Number(a) - Number(b));
      return Number(sortedAnnotationsListKeys[sortedAnnotationsListKeys.length - 1]);
    },
  }))
  .views((self) => ({
    get annotationsListItems() {
      let page = 1;
      const list: AnnotationWithInput[] = [];
      while (self.annotationsListRecords.get(String(page))) {
        list.push(...self.annotationsListRecords.get(String(page)));
        page += 1;
      }
      return list;
    },
    get annotationsListLength() {
      if (!self.lastAnnotationsPageNumber) return 0;

      if (self.hasAnnotationsListReachedEnd) {
        const lastPage = self.annotationsListRecords.get(String(self.lastAnnotationsPageNumber));
        return (self.lastAnnotationsPageNumber - 1) * PER_PAGE + (lastPage?.length || 0);
      }

      return self.lastAnnotationsPageNumber * PER_PAGE;
    },
  }))
  .actions((self) => ({
    getAnnotationScore(annotationId: string) {
      return self.scoresAnnotationsListRecord.get(annotationId);
    },
    setIsAnnotationsLoading(isLoading: typeof self.isAnnotationsLoading) {
      self.isAnnotationsLoading = isLoading;
    },
    setAnnotationsListError(error: typeof self.annotationsListError) {
      self.annotationsListError = error;
    },
  }))
  .actions((self) => ({
    clearAllAnnotationsRecords() {
      self.annotationsListRecords.clear();
      self.scoresAnnotationsListRecord.clear();
    },
    finishLoadingAnnotationsWithError(e: Error) {
      self.setIsAnnotationsLoading(false);
      self.setAnnotationsListError(e);
    },
    areAnnotationsPagesEqual(pageNumber: number, listE: either.Either<Error, MultiSearchResponse>): boolean {
      const respPage = getRespAnnotationsPage(listE);
      const currentPage = self.annotationsListRecords.get(String(pageNumber));
      return isEqual(respPage, currentPage);
    },
    updateAnnotationsListRecordItem(pageNumber?: number, annotationInputsList?: AnnotationWithInput[]) {
      if (pageNumber && annotationInputsList) {
        self.annotationsListRecords.set(String(pageNumber), annotationInputsList);
      }
    },
    resetAnnotationsListStore() {
      self.annotationsListRecords.clear();
      self.scoresAnnotationsListRecord.clear();
      self.isAnnotationsLoading = null;
      self.hasAnnotationsListReachedEnd = null;
      self.lastAnnotationsListPage = null;
      self.currentAnnotationsListPage = 1;
      self.annotationsPagesStartedLoad.replace([]);
      self.annotationsListError = undefined;
    },
  }))
  .actions((self) => ({
    getUserNameByUserId(userId: string): string {
      return self.collaboratorsListMap.get(userId)?.firstName || '';
    },
  }))
  .actions((self) => ({
    setAnnotationsListPage(page: number, listE: either.Either<Error, MultiSearchResponse>): void {
      if (page === 1) self.scoresAnnotationsListRecord.clear();
      return pipe(
        listE,
        either.match(
          // onLeft
          () => {
            console.warn('setAnnotationsListPage: something went wrong');
          },
          // onRight
          (resp) => {
            self.runInAction(() => {
              const annotationsInputsList = resp.getHitsList().map((hit) => {
                const score = hit.getScore();
                const annotation = hit.getAnnotation();
                const annotationId = annotation?.getId();
                if (annotationId) self.scoresAnnotationsListRecord.set(annotationId, score);

                return {
                  annotation,
                  input: hit.getInput(),
                } as AnnotationWithInput;
              });
              if (!annotationsInputsList?.length) {
                // list is empty then reached end
                self.hasAnnotationsListReachedEnd = true;
                const newLastPage = page - 1;
                if (!self.lastAnnotationsListPage || self.lastAnnotationsListPage > newLastPage) {
                  self.lastAnnotationsListPage = newLastPage;
                }
              } else {
                self.updateAnnotationsListRecordItem(Number(page), annotationsInputsList);
                if (annotationsInputsList?.length < PER_PAGE) {
                  // has reached end if less than PER_PAGE length
                  self.hasAnnotationsListReachedEnd = true;
                  self.lastAnnotationsListPage = page;
                }
                if (!self.currentAnnotationsListPage || self.currentAnnotationsListPage < page) {
                  self.currentAnnotationsListPage = page;
                }
              }
            });
          },
        ),
      );
    },
  }))
  .actions((self) => ({
    getAnnotationObjectAndIndexById(inputId: string): { annotationWithInput: AnnotationWithInput; index: number } | null {
      let foundItem = null;
      for (const [pageNumber, inputList] of Array.from(self.annotationsListRecords.entries())) {
        for (const [index, input] of inputList.entries()) {
          if (input.input.getId() === inputId) {
            foundItem = { annotationWithInput: input, index: (Number(pageNumber) - 1) * PER_PAGE + index };
            break;
          }
        }
        if (foundItem) {
          break;
        }
      }
      return foundItem;
    },
    getAnnotationObjectByIndex(index: number): Annotation | undefined {
      const pageNumber = getPageNumberByIndex(index);
      const inputPagedIndex = getInputPagedIndex(index);
      if (pageNumber !== undefined && inputPagedIndex !== undefined) {
        const page = self.annotationsListRecords.get(String(pageNumber));
        if (page) {
          return page[inputPagedIndex].annotation;
        }
      }
      return undefined;
    },
  }));
